import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import Typography from "@mui/material/Typography";
import AddOrderPage from "../../components/Orders/AddOrderPage";
import FilterDialog from "../../components/Orders/FilterDialog";
import OrderPackaging from "../../components/Orders/OrderPackaging";
import PickItemsDialog from "../../components/Orders/PickItemsDialog";
import { searchStyles } from "../../styles/formStyles";
import { useSearchParams } from "react-router-dom";
import { formatOrderStatus, getOrderStatusStyle, formatDisplayDate } from "../../util/Util";
import { IOrderListDTO } from "../../interfaces/Orders";
import { getOrdersList, searchOrder } from "../../services/Orders";
import ViewOrderDetails from "../../components/Orders/ViewOrderDetails";
import { useAuth } from "../../auth/AuthProvider";

const Orders = () => {
  const {getLoginUserInfo} = useAuth();
  const loggedInUser = getLoginUserInfo();
  const [orders, setOrders] = useState<Array<IOrderListDTO>>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [showPackOrder, setShowPackOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrderListDTO | null>(null);
  const [editingOrder, setEditingOrder] = useState<IOrderListDTO | null>(null);
  const [packingOrder, setPackingOrder] = useState<IOrderListDTO | null>(null);
  const [pickItemsOrder, setPickItemsOrder] = useState<IOrderListDTO | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null,);
  const [filters, setFilters] = useState<{
    statuses: string[];
    deliveryFrom: string;
    deliveryTo: string;
    pincodes: string[];
    costFrom: string;
    costTo: string;
  }>({
    statuses: [],
    deliveryFrom: "",
    deliveryTo: "",
    pincodes: [],
    costFrom: "",
    costTo: "",
  });
  const [page, setPage] = useState(1);
  const rowsPerPage = 9;

  const fetchAllOrders = async () => {
    if (!loggedInUser?.companyId) return;
    try {
      setLoading(true);
      const ordersRes = await getOrdersList(loggedInUser.companyId);
      setOrders(Array.isArray(ordersRes) ? ordersRes : []);
    } catch {
      setOrders([]);
    }finally{
      setLoading(false);
    }
  };

  const handleSearch = async (searchKey: string) => {
    if (!searchKey.trim()) {
      fetchAllOrders();
      return;
    }
    try {
      setLoading(true);
      const searchRes = await searchOrder(searchKey);
      setOrders(Array.isArray(searchRes) ? searchRes : []);
      setPage(1);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, 500);
  };
  useEffect(() => {
  fetchAllOrders();
  }, []);
  useEffect(() => {
    if (searchParams.get("action") === "add") {
      setShowAddOrder(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  const paginatedOrders = orders.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );
  const totalPages = Math.ceil(orders.length / rowsPerPage);

  const filterCount =
    filters.statuses.length +
    filters.pincodes.length +
    (filters.costFrom ? 1 : 0) +
    (filters.costTo ? 1 : 0) +
    (filters.deliveryFrom ? 1 : 0) +
    (filters.deliveryTo ? 1 : 0);

  const handleSaveOrder = () => {
    setShowAddOrder(false);
    setEditingOrder(null);
    fetchAllOrders();
  };
  const handleViewClick = (order: any) => {
    setSelectedOrder(order);
    setShowDetailsView(true);
  };

  const handleBackToList = () => {
    setShowDetailsView(false);
    setSelectedOrder(null);
  };

  if (showDetailsView) {
    return <ViewOrderDetails orderData={selectedOrder} onBack={handleBackToList} />;
  }

  if (showAddOrder) {
    return <AddOrderPage order={editingOrder} onSave={handleSaveOrder} onCancel={() => { setShowAddOrder(false); setEditingOrder(null); }} />;
  }

  if (showPackOrder) {
    return <OrderPackaging orderData={packingOrder} onSave={fetchAllOrders} onBack={() => { setShowPackOrder(false); setPackingOrder(null); }} />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        p:1,
        gap: 2,
      }}
    >
      <Paper
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "grey.100",
          }}
        >
          <TextField
            placeholder="Search"
            size="small"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            }}
            sx={searchStyles}
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              startIcon={<FilterAltOutlinedIcon />}
              onClick={(e) => {
                setFilterAnchorEl(e.currentTarget);
                setFilterOpen(true);
              }}
              sx={{
                borderRadius: "6px",
                border: "1px solid rgba(74, 85, 101, 0.10)",
                backgroundColor: "#FFF",
                boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
                textTransform: "none",
                color: "text.primary",
                px: 2,
                "&:hover": { backgroundColor: "#FFF" },
              }}
            >
              Filter{" "}
              {filterCount > 0 && (
                <Typography
                  component="span"
                  sx={{ ml: 0.5, color: "primary.main" }}
                >
                  ({filterCount})
                </Typography>
              )}
            </Button>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={() => {
                setEditingOrder(null);
                setShowAddOrder(true);
              }}
              sx={{ borderRadius: "0.5rem", textTransform: "none", px: 2 }}
            >
              Add Order
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            p:1,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              maxHeight: "500px",
              overflow: "auto", 
              "&::-webkit-scrollbar": { width: "8px", height: "8px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "#CBD5E1",
                borderRadius: "4px",
              },
            }}
          >
            <Table stickyHeader sx={{ minWidth: "1500px" }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Order ID</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Phone Number</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Total Items</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Total Cost</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Delivery Date</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Address</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Pincode</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                      <Typography color="grey.500">No Orders found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedOrders.map((row) => (
                    <TableRow key={row.orderId} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      <TableCell align="center" sx={{ p: 1 }}>
                        {row.orderId}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        {row.phone??""}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        {row.totalItems}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        ₹{row.totalCost}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        {formatDisplayDate(row.deliveryDate)}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1, maxWidth: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <Tooltip title={row.address} arrow>
                          <span>{row.address}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        {row.pincode}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: "0.7rem",
                            py: "0.25rem",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: "16px",
                            textAlign: "center",
                            ...getOrderStatusStyle(row.status),
                            minWidth: "120px",
                          }}
                        >
                          {formatOrderStatus(row.status)}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            gap: 1,
                          }}
                        >
                        <Button
                          startIcon={<VisibilityOutlinedIcon />}
                          sx={{
                            color: "grey.700",
                            textTransform: "none",
                            fontSize: "0.8rem",
                          }}
                          onClick={() => handleViewClick(row)}
                        >
                          View
                        </Button>
                        {row.status === "Unpicked" && (
                          <>
                        <Button
                          startIcon={
                            <Box
                              component="img"
                              src="/edit.svg"
                              alt="Edit"
                              sx={{ width: "1.2rem", height: "1.2rem" }}
                            />
                          }
                          onClick={() => {
                            setEditingOrder(row);
                            setShowAddOrder(true);
                          }}
                          sx={{
                            color: "primary.main",
                            textTransform: "none",
                            fontSize: "0.8rem",
                          }}
                        >
                          Edit
                        </Button>
                        
                            <Button
                              startIcon={<ShoppingBagOutlinedIcon />}
                              onClick={() => setPickItemsOrder(row)}
                              sx={{
                                color: "primary.main",
                                textTransform: "none",
                                fontSize: "0.8rem",
                                "&:hover": { backgroundColor: "transparent" },
                              }}
                            >
                              Pick Items
                            </Button>
                            </>
                        )}
                         {row.status === "Unpacked" && (
                          <>
                            <Button
                              startIcon={<LocalShippingIcon />}
                              onClick={() => { setPackingOrder(row); setShowPackOrder(true); }}
                              sx={{
                                color: "primary.main",
                                textTransform: "none",
                                fontSize: "0.8rem",
                                "&:hover": { backgroundColor: "transparent" },
                              }}
                            >
                              Pack Order
                            </Button>
                        </>
                         )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {orders.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <FilterDialog
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={() => {
          setFilterOpen(false);
          setFilterAnchorEl(null);
        }}
        companyId={loggedInUser?.companyId ?? 0}
        search={searchQuery}
        onApply={(newFilters, data) => {
          setFilters(newFilters);
          setOrders(data);
          setPage(1);
          setFilterOpen(false);
          setFilterAnchorEl(null);
        }}
      />
      <PickItemsDialog
        open={!!pickItemsOrder}
        onClose={() => setPickItemsOrder(null)}
        onSave={fetchAllOrders}
        order={pickItemsOrder}
      />
    </Box>
  );
};
export default Orders;
